import { uid } from 'tools/tools';
import { IDialogItem, IMessagePart, JsonBlock, SearchResults } from '../types';

const HTML_IN_MESSAGE_REGEX = /%BEGIN_HTML%([\s\S]+?(?=%END_HTML%))%END_HTML%\s?/gm;
export const JSON_IN_MESSAGE_REGEX = /%BEGIN_JSON%([\s\S]*?)%END_JSON%/gs;
export const THINKING_IN_MESSAGE_REGEX = /%BEGIN_THINKING%([\s\S]*?)%END_THINKING%/gs;
export const JSON_START_MESSAGE_REGEX = /%BEGIN_(JSON|HTML|THINKING)%/g;
// MI-30342: the model sometimes emits a widget as a markdown ```json fence and drops the
// %BEGIN_JSON% envelope around it; a fence whose body is a renderable widget counts as a block.
const FENCED_JSON_REGEX = /```(?:json)?[ \t]*\r?\n([\s\S]*?)\r?\n[ \t]*```/g;
const FENCED_WIDGET_TYPES = new Set(['hchart', 'chart', 'table']);
const INVALID_REQUEST_REGEX = /^Invalid request:\s*(.+)$/ims;

export const escapeLatexSpecial = (str: string) => str.replace(/(?<!\\)(?:\\\\)*([%&])/g, '\\$1');

export const stripHtmlTags = (text: string | null | undefined): string => {
    // Callers pass fields from streamed/aborted blocks that can be undefined; a bare
    // undefined.replace here crashes the whole app to the global error page.
    return (text ?? '').replace(/<[^>]*>/g, '');
};

type MessageBlock = {
    start: number;
    end: number;
    kind: 'json' | 'thinking' | 'fenced';
    content: string;
    widget?: JsonBlock;
};

// An envelope whose body the model also fenced (`%BEGIN_JSON%```json … ```%END_JSON%`) parses as the
// bare JSON; the fence is a formatting slip, not content.
function stripWrappingFence(body: string): string {
    if (!body.startsWith('```')) {
        return body;
    }
    return body
        .replace(/^```[a-zA-Z]*[ \t]*\r?\n?/, '')
        .replace(/\r?\n?[ \t]*```$/, '')
        .trim();
}

function parseFencedWidget(body: string): JsonBlock | null {
    try {
        const parsed = JSON.parse(body);
        if (parsed && typeof parsed === 'object' && FENCED_WIDGET_TYPES.has(parsed.data_type)) {
            return parsed as JsonBlock;
        }
    } catch {
        // not JSON, or not complete yet while streaming: stays markdown
    }
    return null;
}

function collectAllBlocks(messageData: string): MessageBlock[] {
    JSON_IN_MESSAGE_REGEX.lastIndex = 0;
    THINKING_IN_MESSAGE_REGEX.lastIndex = 0;
    FENCED_JSON_REGEX.lastIndex = 0;

    const toBlock = (m: RegExpMatchArray, kind: 'json' | 'thinking'): MessageBlock => ({
        start: m.index!,
        end: m.index! + m[0].length,
        kind,
        content: kind === 'json' ? stripWrappingFence(m[1].trim()) : m[1].trim(),
    });
    const envelopes = [
        ...[...messageData.matchAll(JSON_IN_MESSAGE_REGEX)].map((m) => toBlock(m, 'json')),
        ...[...messageData.matchAll(THINKING_IN_MESSAGE_REGEX)].map((m) => toBlock(m, 'thinking')),
    ];
    const insideEnvelope = (start: number, end: number) => envelopes.some((b) => start < b.end && end > b.start);

    const fenced: MessageBlock[] = [];
    for (const m of messageData.matchAll(FENCED_JSON_REGEX)) {
        const start = m.index!;
        const end = start + m[0].length;
        if (insideEnvelope(start, end)) {
            continue;
        }
        const widget = parseFencedWidget(m[1].trim());
        if (widget) {
            fenced.push({ start, end, kind: 'fenced', content: m[1].trim(), widget });
        }
    }

    return [...envelopes, ...fenced].sort((a, b) => a.start - b.start);
}

export function parseJsonMessage(messageData: string): IMessagePart[] {
    const messageParts: IMessagePart[] = [];
    const blocks = collectAllBlocks(messageData);

    if (blocks.length === 0) {
        const trimmed = messageData.trim();
        if (trimmed) {
            messageParts.push({
                message: trimmed,
                type: 'markdown',
                source: 'backend',
            });
        }
        return messageParts;
    }

    let currentPosition = 0;

    for (const { start: matchStart, end: matchEnd, kind, content, widget } of blocks) {
        if (matchStart > currentPosition) {
            const textBefore = messageData.slice(currentPosition, matchStart).trim();
            if (textBefore.length > 0) {
                messageParts.push({
                    message: textBefore,
                    type: 'markdown',
                    source: 'backend',
                });
            }
        }

        const blockContent = content;
        if (kind === 'fenced' && widget) {
            messageParts.push({
                message: '',
                type: 'json',
                source: 'backend',
                jsonBlocks: [widget],
            });
        } else if (blockContent) {
            if (kind === 'json') {
                try {
                    const jsonData = JSON.parse(blockContent);
                    // Control-only block: signals turn completion (frontend re-enables input in the
                    // stream reader). It carries no content, so don't emit a renderable part.
                    if (jsonData && jsonData.data_type !== 'turn_complete') {
                        messageParts.push({
                            message: '',
                            type: 'json',
                            source: 'backend',
                            jsonBlocks: [jsonData],
                        });
                    }
                } catch (error) {
                    console.error('JSON parsing error:', blockContent, error);
                }
            } else {
                messageParts.push({
                    message: '',
                    type: 'json',
                    source: 'backend',
                    jsonBlocks: [
                        { data_type: 'stream_thinking', content: [{ label: '', type: 'text', value: blockContent }] },
                    ],
                });
            }
        }

        currentPosition = matchEnd;
    }

    if (currentPosition < messageData.length) {
        const textAfter = messageData.slice(currentPosition).trim();
        if (textAfter.length > 0) {
            messageParts.push({
                message: textAfter,
                type: 'markdown',
                source: 'backend',
            });
        }
    }

    return messageParts;
}

const parseTextParts = (text: string, conversationId: string, messageId: string) => {
    const dataParts = text.split(HTML_IN_MESSAGE_REGEX);
    const parts: IMessagePart[] = [];

    for (let i = 0; i < dataParts.length; i++) {
        // MD parts
        if (i % 2 === 0) {
            const valuePart = dataParts[i];

            if (valuePart === '') {
                continue;
            }

            const match = dataParts[i].match(JSON_START_MESSAGE_REGEX);
            if (match) {
                const index = dataParts[i].indexOf(match[0]);

                const beforeMatch = dataParts[i].slice(0, index);
                if (beforeMatch) {
                    parts.push({
                        message: beforeMatch,
                        type: 'markdown',
                        source: 'backend',
                    });
                }

                parts.push({
                    message: match[0] + dataParts[i].slice(index + match[0].length),
                    type: 'json_start',
                    source: 'backend',
                });

                continue;
            }

            const endsWithPartialMarker = dataParts[i].match(
                /%BE?$|%BEGIN_J?S?O?N?$|%END_J?S?O?N?$|%BEGIN_H?T?M?L?$|%BEGIN_T?H?I?N?K?I?N?G?$|%END_T?H?I?N?K?I?N?G?$/,
            );
            const containsPartialMarker = dataParts[i].match(
                /%BEGIN_(?!JSON%|HTML%|THINKING%)|%END_(?!JSON%|HTML%|THINKING%)/,
            );

            if (endsWithPartialMarker || containsPartialMarker) {
                parts.push({
                    message: dataParts[i],
                    type: 'json_start',
                    source: 'backend',
                });
                continue;
            }

            if (valuePart || dataParts.length === 1) {
                parts.push({
                    message: dataParts[i],
                    type: 'markdown',
                    source: 'backend',
                });
            } else {
                parts.push({
                    message: '',
                    type: 'html',
                    source: 'frontend',
                });
            }
        } else {
            const message = dataParts[i].replace(/%BADGE_ID%/g, `ts_${conversationId}_${messageId}_${i}`);

            // HTML block parts: kept as written here; the display cleans them (tools/safeHtml, security unit 7)
            parts.push({
                message,
                type: 'html',
                source: 'backend',
            });
        }
    }
    return parts;
};

export function parseMessage(messageData: string, conversationId: string, messageId: string = uid()): IMessagePart[] {
    const messageParts: IMessagePart[] = [];

    const blocks = collectAllBlocks(messageData);

    if (blocks.length === 0) {
        return parseTextParts(messageData, conversationId, messageId);
    }

    // Row 2241: the text beside a block is kept as the server wrote it, line breaks and all, and dropped only when it is blank.
    // The stream's chunks are joined into one text, so a trimmed piece lost the blank line that set it apart from the text before
    // it: the line under an answer's table became the table's last row (white 53824).
    let currentPosition = 0;
    for (const { start: matchStart, end: matchEnd, kind, content, widget } of blocks) {
        if (matchStart > currentPosition) {
            const textBefore = messageData.slice(currentPosition, matchStart);
            if (textBefore.trim().length > 0) {
                const textParts = parseTextParts(textBefore, conversationId, messageId);
                messageParts.push(...textParts);
            }
        }

        const blockContent = content;
        if (kind === 'fenced' && widget) {
            messageParts.push({
                message: '',
                type: 'json',
                source: 'backend',
                jsonBlocks: [widget],
            });
        } else if (blockContent) {
            if (kind === 'json') {
                try {
                    const jsonData = JSON.parse(blockContent);
                    // Control-only block (turn completion): no content, do not render.
                    if (jsonData && jsonData.data_type !== 'turn_complete') {
                        messageParts.push({
                            message: '',
                            type: 'json',
                            source: 'backend',
                            jsonBlocks: [jsonData],
                        });
                    }
                } catch (error) {
                    console.error('JSON parse error:', error, blockContent);
                }
            } else {
                messageParts.push({
                    message: '',
                    type: 'json',
                    source: 'backend',
                    jsonBlocks: [
                        { data_type: 'stream_thinking', content: [{ label: '', type: 'text', value: blockContent }] },
                    ],
                });
            }
        }

        currentPosition = matchEnd;
    }

    if (currentPosition < messageData.length) {
        const textAfter = messageData.slice(currentPosition);
        if (textAfter.trim().length > 0) {
            const textParts = parseTextParts(textAfter, conversationId, messageId);
            messageParts.push(...textParts);
        }
    }

    return messageParts;
}

export function processParts(parts: IMessagePart[]): IMessagePart[] {
    const processedParts: IMessagePart[] = [];

    const containStartJson = parts.some((part) => part.type === 'json_start');

    if (!containStartJson) {
        return parts;
    }

    if (containStartJson) {
        return parts.slice(
            0,
            parts.findIndex((part) => part.type === 'json_start'),
        );
    }

    return processedParts;
}

export function findPreviousRequest(dialog: IDialogItem[], currentIndex: number): IDialogItem | null {
    for (let i = currentIndex - 1; i >= 0; i--) {
        if (dialog[i].type === 'request') {
            return dialog[i];
        }
    }
    return null;
}

// A report list relabels its message a search turn only when it IS the answer; a list served as one part of a data answer
// (multi-ask cut 3) leaves the message as it is.
export function relabelsAsSearchTurn(block: SearchResults): boolean {
    return !block.answer_part;
}

export function findResolution(item: IDialogItem): string {
    let resolution = '';
    let result: SearchResults | null = null;

    if (item.type === 'response') {
        const parts = item.parts || [];

        for (const part of parts) {
            if (part.type === 'json' && part.jsonBlocks) {
                for (const block of part.jsonBlocks) {
                    if (block.data_type === 'search_results' && relabelsAsSearchTurn(block as SearchResults)) {
                        result = block as SearchResults;

                        break;
                    }
                }
            }

            if (part.type === 'markdown') {
                resolution = resolution + part.message;
            }
        }
    }

    if (result) {
        resolution = result.search_criteria_summary_phrase;
    }

    return resolution;
}

export interface ParsedMarkdownTable {
    data_type: 'table';
    data: Array<Record<string, any>>;
    /** Row 2120: the header texts in column order; each row's cells are keyed by their column's position (key), never its text. */
    columns?: { key: string; label: string }[];
    sql: string;
    htmlBefore?: string;
    htmlAfter?: string;
}

export function parseMarkdownTable(html: string): ParsedMarkdownTable[] {
    const tables: ParsedMarkdownTable[] = [];
    const tableRegex = /<table>([\s\S]*?)<\/table>/gi;
    let lastIndex = 0;
    let match;

    while ((match = tableRegex.exec(html)) !== null) {
        const tableHtml = match[0];
        const tableStart = match.index;
        const tableEnd = tableRegex.lastIndex;

        const parser = new DOMParser();
        const doc = parser.parseFromString(tableHtml, 'text/html');
        const table = doc.querySelector('table');

        if (!table) continue;

        const headers: string[] = [];
        const headerRow = table.querySelector('thead tr');

        if (headerRow) {
            headerRow.querySelectorAll('th').forEach((th) => {
                headers.push(th.textContent?.trim() || '');
            });
        }

        if (headers.length === 0) continue;

        // Row 2120: a cell is keyed by its column's position, never by the header's text, so columns that share a header
        // ('MoM change' three times) each keep their own values; the header texts ride as the table's columns list.
        const columns = headers.map((label, index) => ({ key: `column_${index}`, label }));
        const data: Array<Record<string, any>> = [];
        const tbody = table.querySelector('tbody');

        if (tbody) {
            tbody.querySelectorAll('tr').forEach((tr) => {
                const row: Record<string, any> = {};
                tr.querySelectorAll('td').forEach((td, index) => {
                    if (index < columns.length) {
                        row[columns[index].key] = td.textContent?.trim() || '';
                    }
                });
                if (Object.keys(row).length > 0) {
                    data.push(row);
                }
            });
            if (data.length > 0) {
                const htmlBefore = lastIndex < tableStart ? html.substring(lastIndex, tableStart) : undefined;

                tables.push({
                    data_type: 'table',
                    data,
                    columns,
                    sql: '',
                    htmlBefore,
                });

                lastIndex = tableEnd;
            }
        }
    }

    if (tables.length > 0) {
        const lastTable = tables[tables.length - 1];
        if (lastIndex < html.length) {
            lastTable.htmlAfter = html.substring(lastIndex);
        }
    }

    return tables;
}
